import type { Unsub } from '../types'

export interface KeepaliveOptions {
  /** how often to ping while visible (ms) */
  interval?: number
  /** ping timeout before we consider the socket dead (ms) */
  timeout?: number
  /** if a wake event finds the last pong older than this, force a reconnect (ms) */
  staleAfter?: number
}

/**
 * Cloudflare drops idle proxied WebSockets at ~100s, and tablets wake from sleep
 * with a dead-but-not-yet-errored socket. This watchdog pings on an interval
 * while visible, and force-reconnects on timeout or on wake (visibility/online/
 * focus) when the connection looks stale. Pauses pinging while hidden.
 */
export class Keepalive {
  private readonly interval: number
  private readonly timeout: number
  private readonly staleAfter: number
  private timer: ReturnType<typeof setInterval> | null = null
  private disposed = false
  private lastPongAt = Date.now()
  private readonly listeners: Unsub[] = []

  constructor(
    private readonly ping: () => Promise<void>,
    private readonly forceReconnect: () => void,
    private readonly onPong: (at: number) => void,
    opts: KeepaliveOptions = {},
  ) {
    this.interval = opts.interval ?? 25_000
    this.timeout = opts.timeout ?? 10_000
    this.staleAfter = opts.staleAfter ?? 45_000
  }

  start(): void {
    this.markPong()
    this.arm()
    const onVisible = () => {
      if (document.visibilityState === 'visible') this.onWake()
      else this.disarm()
    }
    const onWake = () => this.onWake()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onWake)
    window.addEventListener('focus', onWake)
    this.listeners.push(
      () => document.removeEventListener('visibilitychange', onVisible),
      () => window.removeEventListener('online', onWake),
      () => window.removeEventListener('focus', onWake),
    )
  }

  /** call whenever a message/pong confirms the socket is alive */
  markPong(): void {
    this.lastPongAt = Date.now()
    this.onPong(this.lastPongAt)
  }

  dispose(): void {
    this.disposed = true
    this.disarm()
    for (const off of this.listeners) off()
    this.listeners.length = 0
  }

  private arm(): void {
    if (this.timer || this.disposed) return
    this.timer = setInterval(() => void this.beat(), this.interval)
  }

  private disarm(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  private onWake(): void {
    if (this.disposed) return
    this.arm()
    if (Date.now() - this.lastPongAt > this.staleAfter) {
      this.forceReconnect()
    } else {
      void this.beat()
    }
  }

  private async beat(): Promise<void> {
    if (this.disposed || document.visibilityState !== 'visible') return
    try {
      await Promise.race([
        this.ping(),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('ping-timeout')), this.timeout)),
      ])
      this.markPong()
    } catch {
      if (!this.disposed) this.forceReconnect()
    }
  }
}
